import { useMemo, useCallback, useState } from "react";
import TestimonialCard from "@/components/widgets/testimonial-card";
import VideoModal from "@/modules/review/components/video-modal";
import { useWidgetFont } from "../hooks/use-widget-font";
import type { WidgetTemplateProps } from "./template-router";

export default function TemplateClassicCard({ previewData, layoutConfig, templateDetail, brandKit, showBranding = true }: WidgetTemplateProps) {
	const fontStyle = useWidgetFont(layoutConfig, templateDetail);
	const [activeVideoPlaybackId, setActiveVideoPlaybackId] = useState<string | null>(null);

	const getLayoutValue = useCallback(
		(key: string, fallback: unknown = "") => {
			if (layoutConfig[key] !== undefined) return layoutConfig[key];
			if (templateDetail?.defaults?.layout?.[key] !== undefined) return templateDetail.defaults.layout[key];
			return fallback;
		},
		[layoutConfig, templateDetail],
	);

	const review = useMemo(() => {
		if (!previewData) return null;
		if ("reviews" in previewData && previewData.reviews) {
			return previewData.reviews.data[0] || null;
		}
		return null;
	}, [previewData]);

	const showRating = getLayoutValue("show_rating", true) !== false;
	const showAvatar = getLayoutValue("show_avatar", true) !== false;
	const bgColor = String(getLayoutValue("background_color", "") || "");
	const txtColor = String(getLayoutValue("text_color", "") || "");
	const ratingStarColor = String(getLayoutValue("rating_star_color", "") || "") || undefined;
	const accentColor = brandKit?.color_accent_hex || "#6366f1";

	if (!review) {
		return null;
	}

	const isVideoReview = review.content_type === "video" && review.mux_playback_id;

	return (
		<div style={fontStyle}>
			<TestimonialCard
				content={review.content_text || ""}
				authorName={review.customer_name || "Anonymous"}
				authorTitle={
					review.customer_job_title && review.customer_company_name
						? `${review.customer_job_title} at ${review.customer_company_name}`
						: undefined
				}
				avatarUrl={review.customer_avatar_url || undefined}
				rating={(review.rating as 1 | 2 | 3 | 4 | 5) || 5}
				highlightColor={accentColor}
				accentColor={accentColor}
				backgroundColor={bgColor || undefined}
				textColor={txtColor || undefined}
				showRating={showRating}
				showAvatar={showAvatar}
				ratingStarColor={ratingStarColor}
				audioUrl={review.content_type === "audio" ? (review.audio_url || undefined) : undefined}
				audioDuration={review.content_type === "audio" ? review.audio_duration : undefined}
			videoThumbnailUrl={isVideoReview ? (review.video_thumbnail_url || undefined) : undefined}
				onVideoPlay={isVideoReview ? () => review.mux_playback_id && setActiveVideoPlaybackId(review.mux_playback_id) : undefined}
			/>

			{/* Powered by branding */}
			{showBranding && (
				<div className="py-4 flex justify-center text-center text-gray-400">
					<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
						<img src="/images/badge-sayaboutus.svg" alt="build social proof with Say About Us" className="h-5 w-auto" />
					</a>
				</div>
			)}

			{activeVideoPlaybackId && (
				<VideoModal
					open={!!activeVideoPlaybackId}
					onOpenChange={(open) => !open && setActiveVideoPlaybackId(null)}
					playbackId={activeVideoPlaybackId}
				/>
			)}
		</div>
	);
}
